/**
 * useUnits: the display-units preference from tweaks, with the shared
 * formatters and input adapters bound to it. Stored values stay metric;
 * call `toMetres` / `toMps` on commit, `fromMetres` / `fromMps` to seed inputs.
 */

import { useMemo } from 'react';
import * as U from '@twin-builder/units';
import { useTweaks } from '@/hooks/useTweaks';

export function useUnits() {
  const units = useTweaks().tweaks.units;
  return useMemo(
    () => ({
      units,
      fmtLength: (m: number) => U.fmtLength(m, units),
      fmtSpeed: (mps: number) => U.fmtSpeed(mps, units),
      fmtArea: (m2: number) => U.fmtArea(m2, units),
      /** Elevations and altitudes: whole m or ft, never switching to km/mi. */
      fmtElevation: (m: number) => `${Math.round(U.fromMetres(m, units))} ${U.lengthUnit(units)}`,
      lengthUnit: U.lengthUnit(units),
      speedUnit: U.speedUnit(units),
      toMetres: (v: number) => U.toMetres(v, units),
      fromMetres: (m: number) => U.fromMetres(m, units),
      toMps: (v: number) => U.toMps(v, units),
      fromMps: (mps: number) => U.fromMps(mps, units),
    }),
    [units],
  );
}
